import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Alert from '../components/Alert';
import BCPPageTitle from '../components/BCPPageTitle';
import GuideTopAI from '../components/GuideTopAI';
import RedButton from '../components/RedButton';
import RedButtonNext from '../components/RedButtonNext';
import Button from '../parts/button';
import BasicBCPStepFlow from '../components/BasicBCPStepFlow';
import { navigationService } from '../utils/navigation';
import { bcpService } from '../services/bcpService';
import {
  BCPFormContainer,
  BCPFormLayout,
  BCPSectionCard,
  ReferenceMaterialsPanel,
} from '../components/BCP';
import { PROCEDURE_MANUAL_SECTIONS } from '../config/procedureManualSections';
import { useBCPDynamicForm } from '../hooks/useBCPDynamicForm';
import { useAIConsultContext } from '../hooks/useAIConsultContext';
import { useBCPLoader } from '../hooks/useBCPLoader';
import { useAutoDismissMessage } from '../hooks/useAutoDismissMessage';
import { useBeforeUnload } from '../hooks/useBeforeUnload';
import type { ProcedureManualFormData } from '../types/procedure-manual';
import { createInitialProcedureManualFormData } from '../types/procedure-manual';
import { generateStockpile } from '../services/api';
import BCPDownloadModal from '../components/BCPDownloadModal';
import {
  saveBasicBcpSection,
  saveAllDraftSections,
  getInitialResponseDefaultValues,
  getDefaultValuesFromSections,
  loadBasicBcpDraft,
  loadBasicBcpSection
} from '../utils/basicBcpStorage';
import { RESTORE_POLICY_SECTIONS } from '../config/restorePolicySections';
import { REGULATION_SECTIONS } from '../config/regulationSections';

interface NavigationState {
  uploadedFiles: any[];
  apiResponse?: any;
  companyInfo?: {
    companyName: string;
    location: string;
    employeeCount?: string;
    businessDescription: string;
    criticalBusiness: string;
  };
  regulationData?: Record<string, string>;
  restorePolicyData?: Record<string, string>;
  initialResponseData?: Record<string, string>;
  procedureManualData?: ProcedureManualFormData;
  fromPage?: string; // Track where navigation came from (step name or 'bcp-list')
}

const ProcedureManualPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ bcpId?: string }>();
  const [searchParams] = useSearchParams();

  const navigationState = navigationService.extractLocationState<NavigationState>(
    location,
    {
      uploadedFiles: [],
      apiResponse: undefined,
      companyInfo: undefined,
      regulationData: undefined,
      restorePolicyData: undefined,
      initialResponseData: undefined,
      procedureManualData: undefined,
    }
  );

  const { uploadedFiles, procedureManualData, restorePolicyData, companyInfo } = navigationState;

  const [downloadCompleted, setDownloadCompleted] = useState<boolean>(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const { message: successMessage, showMessage: setSuccessMessage } = useAutoDismissMessage();
  const [isGeneratingStockpile, setIsGeneratingStockpile] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(2);
  const hasGeneratedStockpile = useRef(false);
  const hasLoadedInitialData = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const currentGenerationRequest = useRef<AbortController | null>(null);
  // Track latest stockpilePlan value to detect user edits during async generation
  const latestStockpilePlanRef = useRef<string | undefined>(undefined);

  // ページロード時に上部にスクロール
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 「次へ」「戻る」ボタンでのナビゲーション中はブロックしない
  const isNavigatingRef = useRef(false);

  const bcpId = params.bcpId ?? 'new';
  const tenantIdFromQuery = searchParams.get('tenantId');

  // BCP ID and tenant ID resolved values
  const resolvedBcpId = bcpId;
  const resolvedTenantId = tenantIdFromQuery;

  // Load BCP document
  const {
    loading: bcpLoading,
    isEditMode,
    bcpDocument,
    getInitialFormData,
  } = useBCPLoader({
    bcpId: bcpId === 'new' ? null : bcpId,
    tenantId: tenantIdFromQuery,
    onError: (error) => {
      setGeneralErrors([error]);
    },
  });

  // 初期値を保持（変更検知の基準値として使用）
  const initialValuesRef = useRef<Record<string, string> | undefined>(undefined);

  // ダウンロード時に自動保存されたBCPを保持
  const [autoSavedBcp, setAutoSavedBcp] = useState<any>(null);

  // Check if we have saved procedure manual data
  const hasSavedData = useMemo(() => {
    if (procedureManualData && Object.keys(procedureManualData).length > 0) {
      return true;
    }
    // Check if procedure_manual section exists in bcpDocument
    // This is more reliable than checking form_data, as form_data may contain
    // fields from other sections
    if (bcpDocument?.content?.sections?.procedure_manual) {
      return true;
    }
    return false;
  }, [procedureManualData, bcpDocument]);

  // Determine stockpilePlan with proper priority:
  // - Navigation from another step (fromPage is a step name): prefer navigation state (unsaved edits)
  // - Navigation from BCP list (fromPage is 'bcp-list' or 'basic-bcp-list'): prefer server data
  // - No fromPage (direct access or refresh): prefer server data
  // - Empty string is NOT considered valid data - it means "not filled yet" for auto-generation purposes
  // - Only non-empty strings are considered actual data that should prevent auto-generation
  const stockpilePlan = useMemo(() => {
    const fromPage = navigationState.fromPage;
    const isFromBcpList = fromPage === 'bcp-list' || fromPage === 'basic-bcp-list';
    const isFromAnotherStep = fromPage && !isFromBcpList;

    // If coming from another step in the same editing session, trust navigation state
    // This preserves unsaved edits when user navigates between wizard steps
    // Only use non-empty values - empty string means "not filled yet"
    if (isFromAnotherStep && procedureManualData && 'stockpilePlan' in procedureManualData) {
      const navValue = (procedureManualData as any).stockpilePlan;
      if (typeof navValue === 'string' && navValue.trim().length > 0) {
        console.log('[ProcedureManualPage] Using navigation state (from step:', fromPage, ')');
        return navValue;
      }
    }

    // Otherwise, prefer server data in edit mode
    // This prevents stale router cache when coming from BCP list
    // Only use non-empty values - empty string means "not filled yet"
    if (isEditMode && bcpDocument) {
      // Prefer bcpDocument.sections.procedure_manual (most reliable)
      const bcpSectionValue = bcpDocument.content?.sections?.procedure_manual?.stockpilePlan;
      if (typeof bcpSectionValue === 'string' && bcpSectionValue.trim().length > 0) {
        console.log('[ProcedureManualPage] Using server data from content.sections');
        return bcpSectionValue;
      }

      // Fallback to form_data
      const formDataValue = bcpDocument.form_data?.stockpilePlan;
      if (typeof formDataValue === 'string' && formDataValue.trim().length > 0) {
        console.log('[ProcedureManualPage] Using server data from form_data');
        return formDataValue;
      }
    }

    // In create mode, use navigation state if available
    // Only use non-empty values - empty string means "not filled yet"
    if (!isEditMode && procedureManualData && 'stockpilePlan' in procedureManualData) {
      const navValue = (procedureManualData as any).stockpilePlan;
      if (typeof navValue === 'string' && navValue.trim().length > 0) {
        console.log('[ProcedureManualPage] Using navigation state (create mode)');
        return navValue;
      }
    }

    // No saved data exists
    console.log('[ProcedureManualPage] No stockpilePlan data found');
    return undefined;
  }, [isEditMode, bcpDocument, procedureManualData, navigationState.fromPage]);

  // Extract effective restore policy data
  const effectiveRestorePolicyData = useMemo(() => {
    if (restorePolicyData) {
      return restorePolicyData;
    }
    // Extract from bcpDocument if in edit mode
    if (bcpDocument?.content?.sections?.restore_policy) {
      return bcpDocument.content.sections.restore_policy;
    }
    // Fallback to form_data
    if (bcpDocument?.form_data) {
      // Include both new and legacy field names for backward compatibility
      const fields = ['company_info', 'restore_policy_important_business', 'important_business'];
      const extracted: Record<string, any> = {};
      fields.forEach(field => {
        if (bcpDocument.form_data![field]) {
          extracted[field] = bcpDocument.form_data![field];
        }
      });
      return Object.keys(extracted).length > 0 ? extracted : undefined;
    }
    return undefined;
  }, [restorePolicyData, bcpDocument]);

  // Extract effective apiResponse
  const effectiveApiResponse = useMemo(() => {
    if (navigationState.apiResponse) {
      return navigationState.apiResponse;
    }
    // Extract from bcpDocument if needed
    if (bcpDocument?.form_data) {
      const extracted: Record<string, any> = { extracted_info: {} };
      // Try to extract business type from company_info
      const companyInfo = bcpDocument.form_data.company_info;
      if (companyInfo) {
        const businessMatch = companyInfo.match(/事業内容[:\s]*([^\n]+)/);
        if (businessMatch) {
          extracted.extracted_info.business_type = businessMatch[1].trim();
        }
      }
      return extracted;
    }
    return navigationState.apiResponse;
  }, [navigationState.apiResponse, bcpDocument]);

  const form = useBCPDynamicForm({
    sections: PROCEDURE_MANUAL_SECTIONS,
    // stockpilePlanがundefinedでない場合は設定する(空文字列も含む)
    initialValues: stockpilePlan !== undefined ? { stockpilePlan } : {},
  });

  const { values, setFieldValue, errors: fieldErrors, validate } = form;

  // Track latest stockpilePlan value in ref to detect user edits during async generation
  useEffect(() => {
    latestStockpilePlanRef.current = values.stockpilePlan;
  }, [values.stockpilePlan]);

  // isDirtyを計算（初期値と現在の値を比較）
  const isDirty = useMemo(() => {
    if (!initialValuesRef.current) {
      return false;
    }
    return Object.keys(values).some(
      key => values[key] !== (initialValuesRef.current?.[key] || '')
    );
  }, [values]);

  // 離脱確認ダイアログ(フォームが変更されていない、またはエクスポート中はスキップ)
  useBeforeUnload({
    enabled: true,
    shouldBypass: () => {
      // 「次へ」「戻る」ボタンでのナビゲーション中はブロックしない
      if (isNavigatingRef.current) return true;
      return !isDirty || isSaving || downloadLoading || isGeneratingStockpile;
    },
  });

  // BCP ID/Tenant IDの変更を検知してリセット
  const bcpIdRef = useRef(resolvedBcpId);
  const tenantIdRef = useRef(resolvedTenantId);

  useEffect(() => {
    const bcpChanged = resolvedBcpId !== bcpIdRef.current;
    const tenantChanged = resolvedTenantId !== tenantIdRef.current;

    if (bcpChanged || tenantChanged) {
      console.log('[ProcedureManualPage] BCP or tenant changed, resetting state');
      bcpIdRef.current = resolvedBcpId;
      tenantIdRef.current = resolvedTenantId;
      hasLoadedInitialData.current = false;
      hasGeneratedStockpile.current = false;
      setIsGeneratingStockpile(false);
      initialValuesRef.current = undefined;
    }
  }, [resolvedBcpId, resolvedTenantId]);

  const aiConsultContext = useAIConsultContext({
    sections: PROCEDURE_MANUAL_SECTIONS,
    values,
    bcpType: 'basic',
    onFieldValueChange: setFieldValue,
  });

  // Auto-save form data to localStorage (debounced)
  useEffect(() => {
    // Skip if loading or no data loaded yet
    if (!hasLoadedInitialData.current || Object.keys(values).length === 0) {
      return;
    }

    // Skip if no bcpId
    if (resolvedBcpId === 'new') {
      return;
    }

    // Save immediately to ensure latest data is available for PDF download
    // No debounce to prevent timing issues with PDF download
    saveBasicBcpSection(resolvedBcpId, resolvedTenantId, 'procedure_manual', values);
  }, [values, resolvedBcpId, resolvedTenantId]);

  // Load saved data when in edit mode
  useEffect(() => {
    console.log('[ProcedureManualPage] Initial data load effect running', {
      isEditMode,
      hasBcpDocument: !!bcpDocument,
      hasLoadedInitialData: hasLoadedInitialData.current,
      stockpilePlan,
      fromPage: navigationState.fromPage,
    });

    if (isEditMode && bcpDocument && !hasLoadedInitialData.current) {
      console.log('[ProcedureManualPage] Loading initial data...');
      const fromPage = navigationState.fromPage;
      const isFromBcpList = fromPage === 'bcp-list' || fromPage === 'basic-bcp-list';
      const isFromAnotherStep = fromPage && !isFromBcpList;

      let loadedValue: string | undefined = undefined;
      let source = 'none';

      // Priority 1: localStorage (highest priority - contains unsaved edits)
      const savedData = loadBasicBcpSection(resolvedBcpId, resolvedTenantId, 'procedure_manual');
      if (savedData && 'stockpilePlan' in savedData) {
        const localValue = savedData.stockpilePlan;
        if (typeof localValue === 'string' && localValue.trim().length > 0) {
          console.log('[ProcedureManualPage] Loading from localStorage');
          loadedValue = localValue;
          source = 'localStorage';
        }
      }

      // Priority 2: navigation state (if coming from another step, unsaved edits)
      // Only use non-empty values - empty string means "not filled yet"
      if (loadedValue === undefined && isFromAnotherStep && procedureManualData && 'stockpilePlan' in procedureManualData) {
        const navValue = (procedureManualData as any).stockpilePlan;
        if (typeof navValue === 'string' && navValue.trim().length > 0) {
          console.log('[ProcedureManualPage] Loading from navigation state (from step:', fromPage, ')');
          loadedValue = navValue;
          source = 'navigationState';
        }
      }

      // Priority 3: server data (saved in DB)
      // Only use non-empty values - empty string means "not filled yet"
      if (loadedValue === undefined) {
        // First try to get from getInitialFormData (includes form_data and sections merge)
        const rawData = getInitialFormData();

        if ('stockpilePlan' in rawData) {
          const rawValue = rawData.stockpilePlan;
          if (typeof rawValue === 'string' && rawValue.trim().length > 0) {
            loadedValue = rawValue;
            source = 'serverData';
          }
        } else {
          // Fallback: getInitialFormData may not include stockpilePlan if it's only in content.sections
          // Use the stockpilePlan from useMemo which properly checks content.sections
          if (stockpilePlan !== undefined) {
            loadedValue = stockpilePlan;
            source = 'stockpilePlanMemo';
          }
        }
      }

      // Only set field value if we have actual data (non-empty)
      // Empty string or undefined means "not filled yet" and allows auto-generation
      if (typeof loadedValue === 'string' && loadedValue.trim().length > 0) {
        console.log('[ProcedureManualPage] Loading stockpilePlan from', source + ':',
          `${loadedValue.substring(0, 50)}...`);
        setFieldValue('stockpilePlan', loadedValue);
        initialValuesRef.current = { stockpilePlan: loadedValue };
        // Mark as already generated to prevent regeneration
        hasGeneratedStockpile.current = true;
      } else {
        console.log('[ProcedureManualPage] No stockpilePlan data found, will trigger generation');
      }

      // Mark as loaded regardless of whether data exists
      // This allows generation to proceed if stockpilePlan is undefined (not saved yet)
      hasLoadedInitialData.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, bcpDocument, stockpilePlan, navigationState.fromPage]);

  // Extract company_info once for dependency tracking
  const companyInfoText = useMemo(() => {
    return effectiveRestorePolicyData?.company_info;
  }, [effectiveRestorePolicyData]);

  // Extract stable values for stockpile generation
  const criticalBusiness = useMemo(() => {
    if (companyInfo?.criticalBusiness) return companyInfo.criticalBusiness;
    // Support both new field name and legacy field name for backward compatibility
    return effectiveRestorePolicyData?.restore_policy_important_business
      || effectiveRestorePolicyData?.important_business
      || effectiveApiResponse?.extracted_info?.main_business
      || effectiveApiResponse?.extracted_info?.critical_business
      || '';
  }, [companyInfo, effectiveRestorePolicyData, effectiveApiResponse]);

  const businessType = useMemo(() => {
    if (companyInfo?.businessDescription) return companyInfo.businessDescription;
    const extractedInfo = effectiveApiResponse?.extracted_info || {};
    return extractedInfo.business_type || extractedInfo.business_content || '';
  }, [companyInfo, effectiveApiResponse]);

  const companyLocation = useMemo(() => {
    if (companyInfo?.location) return companyInfo.location;
    const extractedInfo = effectiveApiResponse?.extracted_info || {};
    return extractedInfo.address || extractedInfo.location || '';
  }, [companyInfo, effectiveApiResponse]);

  const companyEmployees = useMemo(() => {
    if (companyInfo?.employeeCount) return companyInfo.employeeCount;
    const extractedInfo = effectiveApiResponse?.extracted_info || {};
    return extractedInfo.employees || '';
  }, [companyInfo, effectiveApiResponse]);

  // Determine if we should generate stockpile list
  // Only generate when:
  // - Company info exists
  // - BCP loaded (edit mode) or in create mode
  // - stockpilePlan is undefined (not saved yet from server/navigation)
  // - Not already generated (hasGeneratedStockpile flag)
  // Note: NOT using useMemo because we need to observe Ref changes (hasGeneratedStockpile, hasLoadedInitialData)
  // This is recalculated on every render to ensure Refs are always up-to-date
  const getShouldGenerateStockpile = () => {
    // Wait for BCP document loading to complete
    if (bcpLoading) {
      return false;
    }

    // In edit mode, wait for initial data load
    // However, if we have company info and stockpilePlan is undefined, we can proceed
    // This handles the case where bcpDocument loads slowly but data is already available
    if (isEditMode && !hasLoadedInitialData.current) {
      // If we have all required data, we can proceed without waiting
      if (stockpilePlan === undefined && companyInfoText) {
        // Fall through to continue checks
      } else {
        return false;
      }
    }

    // If stockpile exists (from server or navigation), don't generate
    if (stockpilePlan !== undefined) {
      return false;
    }

    // If already generated once, don't regenerate (unless reset)
    if (hasGeneratedStockpile.current) {
      return false;
    }

    // Must have company info to generate
    if (!companyInfoText || typeof companyInfoText !== 'string') {
      return false;
    }

    return true;
  };

  // Generate stockpile list when conditions are met
  // Uses AbortController to prevent concurrent requests and stale responses
  // Note: AbortController is used for state management and response filtering,
  // not for actual HTTP request cancellation (API doesn't support signal yet)
  useEffect(() => {
    // Evaluate should generate inside effect to observe Ref changes
    const shouldGenerate = getShouldGenerateStockpile();

    if (!shouldGenerate) {
      return;
    }

    console.log('[ProcedureManualPage] Starting stockpile generation', {
      bcpLoading,
      isEditMode,
      hasLoadedInitialData: hasLoadedInitialData.current,
      stockpilePlan,
      hasGeneratedStockpile: hasGeneratedStockpile.current,
      companyInfoText: companyInfoText ? `${companyInfoText.substring(0, 30)}...` : '(empty)',
    });

    // Check if already generating (prevent concurrent requests)
    // Note: We check currentGenerationRequest instead of isGeneratingStockpile to avoid circular deps
    if (currentGenerationRequest.current) {
      console.log('[ProcedureManualPage] Generation already in progress, skipping');
      return;
    }

    // Check if user has already entered data before starting generation
    // This prevents unnecessary API calls when user has typed something
    // Use ref to get the latest value (effect doesn't re-run when values change)
    const currentValue = latestStockpilePlanRef.current;
    if (typeof currentValue === 'string' && currentValue.trim().length > 0) {
      console.log('[ProcedureManualPage] User has already entered stockpilePlan, skipping generation');
      hasGeneratedStockpile.current = true; // Prevent future generation attempts
      return;
    }

    // Capture values at generation start to avoid dependency changes during async operation
    // This prevents the effect from re-running and aborting the request when these values change
    const capturedCompanyInfo = companyInfoText;
    const capturedCriticalBusiness = criticalBusiness;
    const capturedBusinessType = businessType;
    const capturedLocation = companyLocation;
    const capturedEmployees = companyEmployees;
    const capturedSetFieldValue = setFieldValue;

    const abortController = new AbortController();
    currentGenerationRequest.current = abortController;

    const generateStockpileList = async () => {
      hasGeneratedStockpile.current = true;
      setIsGeneratingStockpile(true);

      try {
        console.log('[ProcedureManualPage] Generating stockpile list from restore policy data');

        const response = await generateStockpile({
          company_info: capturedCompanyInfo!,
          critical_business: capturedCriticalBusiness,
          business_type: capturedBusinessType,
          location: capturedLocation,
          employees: capturedEmployees,
        });

        // Safety check: only apply response if request wasn't aborted
        if (abortController.signal.aborted) {
          console.log('[ProcedureManualPage] Stockpile generation aborted, ignoring response');
          return;
        }

        // Check if user has entered data while generation was in progress (non-empty)
        // Empty string is OK to overwrite with generated content
        // This prevents overwriting user's actual input during slow generation
        // Use ref to get the latest value (not stale closure value)
        const latestValue = latestStockpilePlanRef.current;
        if (typeof latestValue === 'string' && latestValue.trim().length > 0) {
          console.log('[ProcedureManualPage] User modified stockpilePlan during generation, keeping user input');
          // Reset flag to allow regeneration if user clears it back to empty later
          hasGeneratedStockpile.current = false;
          return;
        }

        if (response.stockpile_plan) {
          console.log('[ProcedureManualPage] Stockpile list generated successfully, applying to form');
          capturedSetFieldValue('stockpilePlan', response.stockpile_plan);
        }
      } catch (error: any) {
        if (error.name === 'AbortError') {
          console.log('[ProcedureManualPage] Stockpile generation request aborted');
          // Reset flag to allow retry when conditions are met again
          hasGeneratedStockpile.current = false;
          return;
        }
        console.error('[ProcedureManualPage] Failed to generate stockpile list:', error);
        setGeneralErrors(['備蓄品リストの生成に失敗しました。']);
        hasGeneratedStockpile.current = false;
      } finally {
        // Only clear state if this is still the current request
        if (currentGenerationRequest.current === abortController) {
          currentGenerationRequest.current = null;
          setIsGeneratingStockpile(false);
        }
      }
    };

    generateStockpileList();

    // Cleanup: abort request if component unmounts
    return () => {
      if (currentGenerationRequest.current === abortController) {
        console.log('[ProcedureManualPage] Cleaning up stockpile generation request (component unmount)');
        abortController.abort();
        currentGenerationRequest.current = null;
        // IMPORTANT: Reset loading state to prevent stuck spinner
        setIsGeneratingStockpile(false);
        // IMPORTANT: Reset generation flag to allow retry
        hasGeneratedStockpile.current = false;
      }
    };
    // Note: Individual dependencies to trigger re-evaluation when data changes
    // getShouldGenerateStockpile() is called inside effect to observe Ref changes
    // values.stockpilePlan is checked inside effect via latestStockpilePlanRef, not in dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bcpLoading, isEditMode, stockpilePlan, companyInfoText]);

  const handleDownload = async () => {
    // 保存中または他のAPI呼び出し中は早期リターン
    if (isSaving || isGeneratingStockpile) {
      return;
    }

    setCurrentStep(3);

    // ダウンロード前に現在のフォーム内容と全セクションを保存
    setIsSaving(true);
    try {
      console.log('[ProcedureManualPage] Saving all sections before download...');
      let currentBcpId = bcpDocument?.id || resolvedBcpId;

      // First, save procedure_manual to get the actual BCP ID
      // This ensures we have a real BCP ID (not 'new') before saving other sections
      console.log('[ProcedureManualPage] Saving procedure_manual section first...');
      const procedureResult = await bcpService.saveBasicSection(
        currentBcpId === 'new' ? null : currentBcpId,
        'procedure_manual',
        values,
        { current_step: 'procedure_manual' },
        resolvedTenantId || undefined
      );
      currentBcpId = procedureResult.id;
      console.log('[ProcedureManualPage] Procedure manual saved, BCP ID:', currentBcpId);

      // Get default values for all 4 sections
      console.log('[ProcedureManualPage] Getting default values for all sections...');
      const restorePolicyDefaults = getDefaultValuesFromSections(RESTORE_POLICY_SECTIONS);
      const regulationDefaults = getDefaultValuesFromSections(REGULATION_SECTIONS);
      const initialResponseDefaults = getInitialResponseDefaultValues();
      const procedureManualDefaults = getDefaultValuesFromSections(PROCEDURE_MANUAL_SECTIONS);

      console.log('[ProcedureManualPage] Default values extracted:', {
        restore_policy: Object.keys(restorePolicyDefaults).length,
        regulation: Object.keys(regulationDefaults).length,
        initial_response: Object.keys(initialResponseDefaults).length,
        procedure_manual: Object.keys(procedureManualDefaults).length
      });

      // Load from localStorage (user's form input in progress)
      const draftData = loadBasicBcpDraft(currentBcpId, resolvedTenantId);

      // Load from server (already saved data)
      const serverRestorePolicy = bcpDocument?.content?.sections?.restore_policy || {};
      const serverRegulation = bcpDocument?.content?.sections?.regulation || {};
      const serverInitialResponse = bcpDocument?.content?.sections?.initial_response || {};
      const serverProcedureManual = bcpDocument?.content?.sections?.procedure_manual || {};

      // Get localStorage data
      const localRestorePolicy = draftData?.sections?.restore_policy || {};
      const localRegulation = draftData?.sections?.regulation || {};
      const localInitialResponse = draftData?.sections?.initial_response || {};
      const localProcedureManual = draftData?.sections?.procedure_manual || {};

      // Merge with priority: localStorage/current values > server > defaults
      const mergedRestorePolicy = { ...restorePolicyDefaults, ...serverRestorePolicy, ...localRestorePolicy };
      const mergedRegulation = { ...regulationDefaults, ...serverRegulation, ...localRegulation };
      const mergedInitialResponse = { ...initialResponseDefaults, ...serverInitialResponse, ...localInitialResponse };
      const mergedProcedureManual = { ...procedureManualDefaults, ...serverProcedureManual, ...localProcedureManual, ...values };

      console.log('[ProcedureManualPage] Merged section data counts:', {
        restore_policy: Object.keys(mergedRestorePolicy).length,
        regulation: Object.keys(mergedRegulation).length,
        initial_response: Object.keys(mergedInitialResponse).length,
        procedure_manual: Object.keys(mergedProcedureManual).length
      });

      // Save all merged data to localStorage with the REAL BCP ID (not 'new')
      saveBasicBcpSection(currentBcpId, resolvedTenantId, 'restore_policy', mergedRestorePolicy);
      saveBasicBcpSection(currentBcpId, resolvedTenantId, 'regulation', mergedRegulation);
      saveBasicBcpSection(currentBcpId, resolvedTenantId, 'initial_response', mergedInitialResponse);
      saveBasicBcpSection(currentBcpId, resolvedTenantId, 'procedure_manual', mergedProcedureManual);

      console.log('[ProcedureManualPage] Saved all sections to localStorage with BCP ID:', currentBcpId);

      // Save all remaining draft sections from localStorage
      const finalBcpId = await saveAllDraftSections(
        currentBcpId,
        resolvedTenantId,
        bcpService,
        'procedure_manual', // Already saved above, will be skipped
        undefined // Don't pass values since we already saved it
      );

      console.log('[ProcedureManualPage] All sections saved successfully:', finalBcpId);
      setAutoSavedBcp({ id: finalBcpId });
      initialValuesRef.current = { ...values };
    } catch (error) {
      console.error('[ProcedureManualPage] Failed to save all sections:', error);
      setGeneralErrors(['ダウンロード前の保存に失敗しました。']);
      setIsSaving(false);
      return;
    } finally {
      setIsSaving(false);
    }

    setIsDownloadModalOpen(true);
  };

  const handleFieldChange = (fieldId: string, value: string) => {
    setFieldValue(fieldId, value);
    if (generalErrors.length > 0) {
      setGeneralErrors([]);
    }
  };

  const handleSave = async () => {
    // 保存中または他のAPI呼び出し中は早期リターン
    if (isSaving || isGeneratingStockpile) {
      return;
    }

    const isValid = validate();
    if (!isValid) {
      setGeneralErrors(['入力内容に不備があります。各項目を確認してください。']);
      return;
    }

    setIsSaving(true);
    setSubmitLoading(true);
    setSuccessMessage(null);

    try {
      console.log('[ProcedureManualPage] Saving all sections...');
      let currentBcpId = bcpDocument?.id || resolvedBcpId;

      // First, save procedure_manual to get the actual BCP ID
      console.log('[ProcedureManualPage] Saving procedure_manual section first...');
      const procedureResult = await bcpService.saveBasicSection(
        currentBcpId === 'new' ? null : currentBcpId,
        'procedure_manual',
        values,
        { current_step: 'procedure_manual' },
        resolvedTenantId || undefined
      );
      currentBcpId = procedureResult.id;
      console.log('[ProcedureManualPage] Procedure manual saved, BCP ID:', currentBcpId);

      // Get default values for all 4 sections
      console.log('[ProcedureManualPage] Getting default values for all sections...');
      const restorePolicyDefaults = getDefaultValuesFromSections(RESTORE_POLICY_SECTIONS);
      const regulationDefaults = getDefaultValuesFromSections(REGULATION_SECTIONS);
      const initialResponseDefaults = getInitialResponseDefaultValues();
      const procedureManualDefaults = getDefaultValuesFromSections(PROCEDURE_MANUAL_SECTIONS);

      // Load from localStorage (user's form input in progress)
      const draftData = loadBasicBcpDraft(currentBcpId, resolvedTenantId);

      // Load from server (already saved data)
      const serverRestorePolicy = bcpDocument?.content?.sections?.restore_policy || {};
      const serverRegulation = bcpDocument?.content?.sections?.regulation || {};
      const serverInitialResponse = bcpDocument?.content?.sections?.initial_response || {};
      const serverProcedureManual = bcpDocument?.content?.sections?.procedure_manual || {};

      // Get localStorage data
      const localRestorePolicy = draftData?.sections?.restore_policy || {};
      const localRegulation = draftData?.sections?.regulation || {};
      const localInitialResponse = draftData?.sections?.initial_response || {};
      const localProcedureManual = draftData?.sections?.procedure_manual || {};

      // Merge with priority: localStorage/current values > server > defaults
      const mergedRestorePolicy = { ...restorePolicyDefaults, ...serverRestorePolicy, ...localRestorePolicy };
      const mergedRegulation = { ...regulationDefaults, ...serverRegulation, ...localRegulation };
      const mergedInitialResponse = { ...initialResponseDefaults, ...serverInitialResponse, ...localInitialResponse };
      const mergedProcedureManual = { ...procedureManualDefaults, ...serverProcedureManual, ...localProcedureManual, ...values };

      console.log('[ProcedureManualPage] Merged section data counts:', {
        restore_policy: Object.keys(mergedRestorePolicy).length,
        regulation: Object.keys(mergedRegulation).length,
        initial_response: Object.keys(mergedInitialResponse).length,
        procedure_manual: Object.keys(mergedProcedureManual).length
      });

      // Save all merged data to localStorage with the REAL BCP ID (not 'new')
      saveBasicBcpSection(currentBcpId, resolvedTenantId, 'restore_policy', mergedRestorePolicy);
      saveBasicBcpSection(currentBcpId, resolvedTenantId, 'regulation', mergedRegulation);
      saveBasicBcpSection(currentBcpId, resolvedTenantId, 'initial_response', mergedInitialResponse);
      saveBasicBcpSection(currentBcpId, resolvedTenantId, 'procedure_manual', mergedProcedureManual);

      console.log('[ProcedureManualPage] Saved all sections to localStorage with BCP ID:', currentBcpId);

      // Save all remaining draft sections from localStorage
      const finalBcpId = await saveAllDraftSections(
        currentBcpId,
        resolvedTenantId,
        bcpService,
        'procedure_manual', // Already saved above, will be skipped
        undefined // Don't pass values since we already saved it
      );

      console.log('[ProcedureManualPage] All sections saved successfully:', finalBcpId);

      // 保存成功後、現在の値を新しい初期値として設定
      initialValuesRef.current = { ...values };

      // Update bcpId if this was a new document
      if (bcpId === 'new' && finalBcpId) {
        navigate(`/bcp/${finalBcpId}/procedure-manual`, {
          state: {
            ...location.state,
            bcpId: finalBcpId,
          },
          replace: true,
        });
      }

      setSuccessMessage('手順書データを保存しました。');
    } catch (error) {
      console.error('Error saving procedure manual:', error);
      setGeneralErrors(['手順書データの保存に失敗しました。']);
    } finally {
      setSubmitLoading(false);
      setIsSaving(false);
    }
  };

  const handleSubmit = () => {
    // 「次へ」ボタンでのナビゲーション開始（ブロック解除）
    isNavigatingRef.current = true;

    setCurrentStep(3);
    setIsDownloadModalOpen(true);
  };

  const handleStepNavigate = async (targetStep: string) => {
    // Mark as internal navigation to bypass beforeunload
    isNavigatingRef.current = true;

    // Save current form data to localStorage before navigation
    if (bcpId !== 'new') {
      console.log('[ProcedureManualPage] Saving form data to localStorage before step navigation');
      saveBasicBcpSection(bcpId, resolvedTenantId, 'procedure_manual', values);
    }

    // Build target URL with tenantId
    let targetUrl = `/bcp/${bcpId}/${targetStep}`;
    if (resolvedTenantId) {
      targetUrl += `?tenantId=${resolvedTenantId}`;
    }

    console.log('[ProcedureManualPage] Navigating to step:', targetStep, 'with state');

    // Navigate with complete state
    navigate(targetUrl, {
      state: {
        ...location.state,
        procedureManualData: values,
        fromPage: 'procedure-manual',
      },
    });
  };

  const aggregatedFieldErrors = useMemo(
    () => Object.values(fieldErrors),
    [fieldErrors]
  );

  return (
    <>
    <BCPFormLayout
      currentStep={currentStep}
      showHeaderActions={true}
      onSave={handleSave}
      onDownload={handleDownload}
      saveLoading={submitLoading}
      downloadLoading={downloadLoading}
      saveDisabled={isGeneratingStockpile}
      downloadDisabled={isGeneratingStockpile}
      fieldContext={aiConsultContext.fieldContext}
      onAIContentApply={aiConsultContext.handleAIContentApply}
      bcpType="basic"
      bcpId={resolvedBcpId}
      topContent={
        <BasicBCPStepFlow
          currentStep="procedure-manual"
          bcpId={bcpId}
          tenantId={tenantIdFromQuery}
          onStepNavigate={handleStepNavigate}
        />
      }
    >
      <BCPFormContainer
        header={
          <>
            <BCPPageTitle title="基本版BCPシート（手順書）" />
            <div className="mt-6 flex justify-center">
              <GuideTopAI />
            </div>
          </>
        }
        alerts={
          <div className="space-y-4">
            {isGeneratingStockpile && (
              <Alert
                type="info"
                message="備蓄品リストを生成しています..."
              />
            )}

            {successMessage && <Alert type="success" message={successMessage} />}

            {generalErrors.length > 0 && (
              <Alert
                type="error"
                title="エラーが発生しました"
                messages={generalErrors}
              />
            )}

            {aggregatedFieldErrors.length > 0 && (
              <Alert
                type="error"
                title="入力内容を確認してください"
                messages={aggregatedFieldErrors}
              />
            )}
          </div>
        }
        footer={
          <>
            <ReferenceMaterialsPanel uploadedFiles={uploadedFiles} className="mt-6" />
            <div className="mt-8 flex justify-center gap-5">
              <RedButton
                onClick={() => {
                  // 「戻る」ボタンでのナビゲーション開始（ブロック解除）
                  isNavigatingRef.current = true;
                  navigate(`/bcp/${bcpId}/initial-response`, {
                    state: {
                      ...location.state,
                      procedureManualData: values,
                      fromPage: 'procedure-manual',
                    },
                  });
                }}
              >
                戻る
              </RedButton>
              <RedButtonNext onClick={handleSubmit} loading={submitLoading}>
                完成・ダウンロード
              </RedButtonNext>
            </div>
          </>
        }
      >
        {PROCEDURE_MANUAL_SECTIONS.map((section) => (
          <BCPSectionCard
            key={section.id}
            section={section}
            values={values}
            errors={fieldErrors}
            onChange={handleFieldChange}
            onAIConsult={aiConsultContext.handleAIConsult}
          />
        ))}
      </BCPFormContainer>
    </BCPFormLayout>

    {/* Download Modal */}
    <BCPDownloadModal
      isOpen={isDownloadModalOpen}
      onClose={() => {
        setIsDownloadModalOpen(false);
        setCurrentStep(2);
      }}
      bcpType="basic"
      bcpId={autoSavedBcp?.id || bcpDocument?.id || (bcpId !== 'new' ? bcpId : null)}
      tenantId={resolvedTenantId}
    />
  </>
  );
};

export default ProcedureManualPage;
